import React from 'react';
import PageLoader from '../../../common/PageLoader';
import { applySort, SORT, useUserCoords } from '../../../../utils/sortListings';
import { Link } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.css';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getGarageDataWFilter,
  getQuickServicesListing,
} from './../../../../Redux/Actions/UserAction';
import { useTranslation } from 'react-i18next';
import SortControl from '../../../common/SortControl';
import ServiceFilterPanel, {
  EMPTY_SERVICE_FILTER,
  isServiceFilterActive,
  serviceMatchesFilter,
  serviceOptionsFromRows,
  brandOptionsFromRows,
} from '../../../common/ServiceFilterPanel';

export default function QuickServices() {
  const { t, i18n } = useTranslation();
  const isRTL = (i18n.language || 'en').toLowerCase().startsWith('ar');
  const dispatch = useDispatch();
  const home = useSelector((state) => state?.home);
  const getQuickServicesList = home?.getQuickServicesList;

  const [sortOption, setSortOption] = useState(SORT.DISTANCE); // [CF 2026-08-02] nearest-first by default
  // [CF 2026-08-02] Only asks the browser for a location when the user
  // actually picks the distance sort; cached for the session after that.
  const userCoords = useUserCoords(sortOption === SORT.DISTANCE);
  // [CF 2026-08-02] Now possible: the quickServiceOwner listing returns each
  // owner's services (backend fix the same day) — before it returned none.
  const [svcFilter, setSvcFilter] = useState({ ...EMPTY_SERVICE_FILTER });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    dispatch(getQuickServicesListing());
  }, [dispatch]);

  const visibleList = React.useMemo(() => {
    if (!getQuickServicesList) return undefined;
    let list = [...getQuickServicesList];
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((x) =>
        (x.quickServiceOwnerName || '').toLowerCase().includes(q)
      );
    }
    list = list.filter((x) => serviceMatchesFilter(x, svcFilter));
    list = applySort(list, sortOption, {
      name: (x) => x.quickServiceOwnerName,
      coords: userCoords,
    });
    return list;
  }, [getQuickServicesList, sortOption, userCoords, search, svcFilter]);

  const serviceOptions = React.useMemo(
    () => serviceOptionsFromRows(getQuickServicesList, isRTL),
    [getQuickServicesList, isRTL]
  );
  const brandOptions = React.useMemo(
    () => brandOptionsFromRows(getQuickServicesList, isRTL),
    [getQuickServicesList, isRTL]
  );

  // Loading state
  if (getQuickServicesList === undefined) {
    return (
      <div className="text-center text-warning" style={{ padding: '80px' }}>
        <PageLoader />
      </div>
    );
  }

  // If no quick services found after loading
  if (!getQuickServicesList || getQuickServicesList.length === 0) {
    return (
      <div
        className="text-center"
        style={{
          padding: '50px',
        }}
      >
        <h4>{t('No Quick Service found')}</h4>
        <p>
          {t('No Quick Service is available right now. Please visit later')}
        </p>
      </div>
    );
  }

  return (
    <div>
      <div class="main-content-area">
        <div class="container">
          <h2 className="mb-2">{t('Quick Services')}</h2>

          <div
            className="filter-row mb-4 d-flex align-items-center flex-nowrap"
            style={{ gap: '14px' }}
          >
            {/* Sort By — left */}
            <SortControl value={sortOption} onChange={setSortOption} />

            {/* [CF 2026-08-02] Filter funnel — panel opens on its own row below. */}
            <button
              type="button"
              onClick={() => setFiltersOpen((o) => !o)}
              aria-label={t('Filter')}
              title={t('Filter')}
              style={{
                width: '42px',
                height: '42px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: 'none',
                borderRadius: '50%',
                background: isServiceFilterActive(svcFilter) ? '#fff7e6' : 'transparent',
                cursor: 'pointer',
                flex: '0 0 auto',
              }}
            >
              <img
                src="assets/images/icons/filter.png"
                alt={t('Filter')}
                style={{ width: '22px', height: '22px' }}
              />
            </button>

            {/* Search (icon → expands) + result count — fills remaining space on the same row */}
            <div
              className="d-flex align-items-center"
              style={{
                gap: '12px',
                minWidth: 0,
                flex: '1 1 auto',
                justifyContent: 'flex-end',
              }}
            >
              {searchOpen ? (
                <div
                  style={{
                    position: 'relative',
                    flex: '1 1 auto',
                    maxWidth: '460px',
                    minWidth: 0,
                  }}
                >
                  <input
                    type="text"
                    autoFocus
                    className="form-control"
                    placeholder={t('Search by name')}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ paddingInlineEnd: '34px' }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setSearch('');
                      setSearchOpen(false);
                    }}
                    aria-label={t('Clear')}
                    style={{
                      position: 'absolute',
                      insetInlineEnd: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      fontSize: '20px',
                      lineHeight: 1,
                      color: '#666',
                    }}
                  >
                    &times;
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setSearchOpen(true)}
                  aria-label={t('Search by name')}
                  style={{
                    width: '42px',
                    height: '42px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: 'none',
                    borderRadius: '50%',
                    background: 'transparent',
                    cursor: 'pointer',
                  }}
                >
                  <img src="assets/images/icons/search.png" alt={t('Search by name')} style={{ width: '22px', height: '22px' }} />
                </button>
              )}
            </div>
          </div>

          {filtersOpen && (
            <ServiceFilterPanel
              value={svcFilter}
              onChange={setSvcFilter}
              onApply={() => setFiltersOpen(false)}
              fields={['service', 'brand', 'openNow']}
              serviceOptions={serviceOptions}
              brandOptions={brandOptions}
            />
          )}

          {visibleList && visibleList.length === 0 ? (
            <p className="text-muted">{t('No results match your search.')}</p>
          ) : (
            <div className="row">
              {(visibleList || []).map((item, i) => {
                const logo = item?.businessMedia?.find(
                  (media) => media?.mediaType === 'LOGO'
                )?.media?.path;
                return (
                  <div
                    key={item?.quickServiceOwnerId || i}
                    className="col-4 col-sm-4 col-md-4 col-lg-3 mb-4"
                  >
                    <div className="user-car-wash-block">
                      <Link
                        to={`/user-quick-services-details/${item.quickServiceOwnerId}`}
                      >
                        <div
                          className="line-a"
                          style={{
                            width: '100%',
                            aspectRatio: '1 / 1',
                            overflow: 'hidden',
                            borderRadius: '50%',   // [CF] client: all logos rounded
                            background: '#f4f4f4',
                          }}
                        >
                          <img
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                            src={logo || 'assets/images/user/taxi/user.jpg'}
                            alt={item?.quickServiceOwnerName || ''}
                            loading="lazy"
                            decoding="async"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'assets/images/user/taxi/user.jpg';
                            }}
                          />
                        </div>
                        <div className="line-b"> {item?.quickServiceOwnerName}</div>
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
